import { useCallback, useState } from "react";
import { useMoveFileMutation, useDeleteFilesMutation } from "@/api/files";
import { toast } from "react-toastify";
import { Item, FolderItem } from "@/types/fse";
import * as analytics from "@/services/analytics";

interface UseMoveDeleteProps {
  items: Item[];
  selectedItemIds: Set<string>;
  clearSelection: () => void;
  canMutateItem?: (item: Item) => boolean;
}

export const getMutableSelectedItems = (
  items: Item[],
  selectedItemIds: Set<string>,
  canMutateItem?: (item: Item) => boolean,
): Item[] => items.filter(item => selectedItemIds.has(item.id) && (canMutateItem?.(item) ?? true));

export const useMoveDelete = ({ items, selectedItemIds, clearSelection, canMutateItem }: UseMoveDeleteProps) => {
  const [moveFile, { isLoading: isMoveFileLoading }] = useMoveFileMutation();
  const [deleteFiles, { isLoading: isDeleteLoading }] = useDeleteFilesMutation();
  const [movingItemIds, setMovingItemIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleMoveItems = useCallback(
    async (draggedItem: Item, targetFolder: FolderItem) => {
      if (!(canMutateItem?.(draggedItem) ?? true) || !(canMutateItem?.(targetFolder) ?? true)) return;
      const itemsToMove = selectedItemIds.has(draggedItem.id)
        ? getMutableSelectedItems(items, selectedItemIds, canMutateItem)
        : [draggedItem];

      const itemIdsToMove = itemsToMove.map(item => item.id);
      setMovingItemIds(prev => new Set([...prev, ...itemIdsToMove]));

      try {
        const movePromises = itemsToMove.map(item => {
          if (item.parent_fs_id === targetFolder.id) {
            return Promise.reject(new Error(`"${item.name}" is already in this folder`));
          }

          return moveFile({
            fse_id: item.id,
            target_parent_id: targetFolder.id,
          }).unwrap();
        });

        await Promise.all(movePromises);

        itemsToMove.forEach(item => {
          analytics.documentMove(item.id, item.name, targetFolder.name, targetFolder.id);
        });

        const itemCount = itemsToMove.length;
        if (itemCount === 1) {
          toast.success(`"${itemsToMove[0].name}" moved to "${targetFolder.name}"`);
        } else {
          toast.success(`${itemCount} items moved to "${targetFolder.name}"`);
        }

        clearSelection();
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error(`Failed to move ${itemsToMove.length === 1 ? "item" : "items"}`);
        }
      } finally {
        setMovingItemIds(prev => {
          const newSet = new Set(prev);
          itemIdsToMove.forEach(id => newSet.delete(id));
          return newSet;
        });
      }
    },
    [canMutateItem, moveFile, selectedItemIds, items, clearSelection],
  );

  const handleDeleteClick = useCallback(() => {
    if (selectedItemIds.size > 0) {
      setShowDeleteConfirmation(true);
    }
  }, [selectedItemIds.size]);

  const handleConfirmDelete = useCallback(async () => {
    // Collect selected items for deletion (both files and folders)
    const itemsToDelete = getMutableSelectedItems(items, selectedItemIds, canMutateItem).map(item => item.id);

    // If no items selected, do nothing
    if (itemsToDelete.length === 0) {
      setShowDeleteConfirmation(false);
      return;
    }

    try {
      // Call the delete mutation
      await deleteFiles({ fse_ids: itemsToDelete }).unwrap();

      items
        .filter(item => selectedItemIds.has(item.id))
        .forEach(item => {
          if (item.type === "folder") {
            analytics.folderRemove(item.id, item.name);
          } else {
            analytics.documentRemove(item.id, item.name);
          }
        });

      // Show success toast
      const deletedCount = itemsToDelete.length;
      if (deletedCount === 1) {
        toast.success("Item deleted successfully");
      } else {
        toast.success(`${deletedCount} items deleted successfully`);
      }

      // Clear the selection
      clearSelection();
    } catch (error) {
      // Handle error
      toast.error(error instanceof Error ? error.message : "Failed to delete items");
    } finally {
      setShowDeleteConfirmation(false);
    }
  }, [canMutateItem, deleteFiles, selectedItemIds, items, clearSelection]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  return {
    // Move operations
    handleMoveItems,
    movingItemIds,
    isMoveFileLoading,

    // Delete operations
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    showDeleteConfirmation,
    isDeleteLoading,
  };
};
